import React, { useState, useEffect, useRef } from 'react';
import { X, Check } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';

const OFFICES = {
  europe: { number: '33767735161', flag: '\u{1F1EB}\u{1F1F7}' },
  uae: { number: '971585070177', flag: '\u{1F1E6}\u{1F1EA}' },
};

// Routage automatique par langue, avec possibilité de changer au clic
const PREFERRED = { fr: 'europe', it: 'europe', ar: 'uae', en: 'uae' };

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.347-.347.52-.52.174-.174.232-.298.347-.497.116-.198.058-.371-.014-.52-.072-.148-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884a9.82 9.82 0 016.988 2.898 9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

export const WhatsAppButton = () => {
  const { t, lang, dir } = useLang();
  const isRtl = (dir || t.dir) === 'rtl';
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  const preferred = PREFERRED[lang] || 'europe';
  const order = preferred === 'europe' ? ['europe', 'uae'] : ['uae', 'europe'];

  useEffect(() => {
    const onClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const linkFor = (key) =>
    `https://wa.me/${OFFICES[key].number}?text=${encodeURIComponent(t.whatsapp.message)}`;

  return (
    <div
      ref={panelRef}
      className={`fixed bottom-6 z-[90] ${isRtl ? 'left-6' : 'right-6'}`}
      data-testid="whatsapp-widget"
    >
      {open && (
        <div
          className="mb-3 w-[19rem] rounded-2xl bg-[#0b0b0d]/95 backdrop-blur-xl border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.6)] overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300"
          data-testid="whatsapp-panel"
        >
          <div className="flex items-start justify-between px-5 pt-5 pb-3">
            <div>
              <p className="text-white text-sm font-semibold">{t.whatsapp.title}</p>
              <p className="text-gray-500 text-xs mt-0.5">{t.whatsapp.subtitle}</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label={t.gallery.close}
              className="text-gray-500 hover:text-white transition-colors"
              data-testid="whatsapp-panel-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="px-3 pb-3 space-y-2">
            {order.map((key) => (
              <a
                key={key}
                href={linkFor(key)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-xl px-3 py-3 bg-white/[0.03] border border-white/5 hover:border-[#25D366]/50 hover:bg-[#25D366]/5 transition-all duration-200 group"
                data-testid={`whatsapp-office-${key}`}
              >
                <span className="text-xl leading-none">{OFFICES[key].flag}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-white text-sm font-medium truncate">
                    {key === 'europe' ? t.whatsapp.europe : t.whatsapp.uae}
                  </span>
                  <span className="block text-gray-500 text-xs truncate">
                    {key === 'europe' ? t.whatsapp.europeDesc : t.whatsapp.uaeDesc}
                  </span>
                </span>
                {key === preferred && (
                  <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#25D366] whitespace-nowrap">
                    <Check className="w-3 h-3" />
                    {t.whatsapp.recommended}
                  </span>
                )}
              </a>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={t.whatsapp.open}
        title={t.whatsapp.open}
        className={`relative w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-[0_10px_30px_rgba(37,211,102,0.35)] hover:scale-105 active:scale-95 transition-transform duration-200 ${isRtl ? 'mr-auto' : 'ml-auto'}`}
        data-testid="whatsapp-fab"
      >
        <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-40 animate-ping" />
        <WhatsAppIcon className="relative w-7 h-7" />
      </button>
    </div>
  );
};

export default WhatsAppButton;
